import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'
import {unitOfTime} from 'moment'
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs'
import {
  distinctUntilChanged,
  first,
  map,
  skip,
  takeUntil,
  tap,
} from 'rxjs/operators'
import {filterTypeValue} from '../search-filter.component'

export interface SinceOptions {
  label: string
  value: {
    timeUnit: unitOfTime.Base
    min: number
    max: number
  }
}

@Component({
  selector: 'mypayter-portal-since',
  templateUrl: './since.component.html',
  styleUrls: ['./since.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SinceComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: SinceComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class SinceComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() filterType$: Observable<filterTypeValue>
  form: FormGroup
  destroy$ = new Subject<void>()
  showForm$ = new BehaviorSubject(false)
  initialised = false
  options$: Observable<SinceOptions[]> = of([
    {
      label: 'hours',
      value: {
        timeUnit: 'hours',
        min: 0,
        max: Infinity,
      },
    },
    {
      label: 'days',
      value: {
        timeUnit: 'days',
        min: 0,
        max: Infinity,
      },
    },
  ])

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group(
      {
        timeUnit: this.fb.control(null),
        amount: this.fb.control(1),
      },
      {
        validators: this.validateMinMax,
      },
    )
  }
  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  writeValue(obj: any): void {
    if (!this.initialised) {
      return
    }
    if (obj && obj.hasOwnProperty('timeUnit') && obj.hasOwnProperty('amount')) {
      this.options$
        .pipe(
          first(),
          tap((r) =>
            this.form.patchValue({
              timeUnit: r.find((item) => item.value.timeUnit === obj.timeUnit),
              amount: Math.abs(obj.amount),
            }),
          ),
        )
        .subscribe()
    } else {
      this.options$
        .pipe(
          first(),
          tap((r) => this.form.patchValue({timeUnit: r[0].value, amount: 1})), // set the first option
        )
        .subscribe()
    }
  }
  registerOnChange(fn: any): void {
    combineLatest([
      this.form.valueChanges.pipe(
        skip(1),
        takeUntil(this.destroy$),
        distinctUntilChanged(),
      ),
      this.filterType$.pipe(takeUntil(this.destroy$)),
    ])
      .pipe(
        map(([r, filterType]) => {
          return {
            timeUnit: r.timeUnit.timeUnit,
            amount:
              filterType === filterTypeValue.PRIOR_TO
                ? r.amount * -1
                : r.amount,
          }
        }),
      )
      .subscribe(fn)
  }
  registerOnTouched(fn: any): void {
    // throw new Error('Method not implemented.');
  }
  setDisabledState?(isDisabled: boolean): void {
    // throw new Error('Method not implemented.');
  }

  ngOnInit() {
    this.options$
      .pipe(
        tap((r) =>
          this.form.patchValue({timeUnit: r[0].value}, {emitEvent: false}),
        ), // set the first option
      )
      .subscribe()
    this.form.get('timeUnit').valueChanges.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
      tap((_) => this.form.patchValue({amount: 1}, {emitEvent: false})),
    )
    // this seems to smoothen the loading
    this.showForm$.next(true)
    this.initialised = true
  }
  validateMinMax(control: AbstractControl) {
    if (!!!control.value.amount) {
      return {error: 'Cannot be empty or 0'}
    }
    if (control.value.timeUnit !== null) {
      if (
        control.value.amount > control.value.timeUnit.max ||
        control.value.amount < control.value.timeUnit.min
      ) {
        return {
          error: `Must be between ${control.value.timeUnit.min} and ${control.value.timeUnit.max}`,
        }
      }
    }
    return null
  }
  validate(ctrl: AbstractControl) {
    return this.form.errors
  }
}
